import React, { useState, useEffect } from 'react';
import { Form, Input, Button, Card, Typography, message } from 'antd';
import { UserOutlined, LockOutlined, SecurityScanOutlined } from '@ant-design/icons';
import { useAuth } from '../../contexts/AuthContext';
import './Login.css';

const { Title, Text } = Typography;

const Login = () => {
  const [loading, setLoading] = useState(false);
  const [step, setStep] = useState('credentials');
  const [userId, setUserId] = useState(null);
  const [code, setCode] = useState('');
  const [form] = Form.useForm();
  const { login, verify2fa } = useAuth();

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('session_expired') === '1') {
      message.warning('Your session has expired. Please login again.');
      window.history.replaceState({}, document.title, '/');
    }
  }, []);

  const onFinish = async (values) => {
    setLoading(true);
    try {
      const result = await login(values);
      if (result && result.requires_2fa) {
        setUserId(result.user_id);
        setStep('verify');
      } else if (result) {
        message.success('Login successful!');
      }
    } catch (error) {
      message.error('Login failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const onVerify2fa = async () => {
    if (!code || code.length !== 6) {
      message.warning('Please enter a valid 6-digit code');
      return;
    }
    setLoading(true);
    try {
      const success = await verify2fa(userId, code);
      if (!success) {
        message.error('Verification failed');
      }
    } catch (error) {
      message.error('Verification failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="login-container">
      <Card className="login-card">
        <div className="login-header">
          <img
            src="/images/kankali-logo.png"
            alt="Kankali Mart"
            style={{ height: '60px', width: 'auto', marginBottom: 8 }}
            onError={(e) => {
              e.target.src =
                'data:image/svg+xml,%3Csvg xmlns=%22http://www.w3.org/2000/svg%22 viewBox=%220 0 100 100%22%3E%3Crect width=%22100%22 height=%22100%22 rx=%2220%22 fill=%22%232d5a3d%22/%3E%3Ctext x=%2250%22 y=%2268%22 font-family=%22Arial,sans-serif%22 font-size=%2244%22 font-weight=%22bold%22 fill=%22white%22 text-anchor=%22middle%22%3ECC%3C/text%3E%3C/svg%3E';
            }}
          />
          {step === 'credentials' ? (
            <>
              <Title level={3} style={{ margin: 0 }}>
                Delivery Login
              </Title>
              <Text type="secondary">Delivery Personnel Portal</Text>
            </>
          ) : (
            <>
              <Title level={3} style={{ margin: 0 }}>
                Two-Factor Auth
              </Title>
              <Text type="secondary">Enter the code from your authenticator app</Text>
            </>
          )}
        </div>

        {step === 'credentials' ? (
          <Form form={form} name="login" onFinish={onFinish} layout="vertical" size="large">
            <Form.Item
              name="login"
              rules={[{ required: true, message: 'Please input your email or phone!' }]}
            >
              <Input prefix={<UserOutlined />} placeholder="Email or Phone Number" />
            </Form.Item>

            <Form.Item
              name="password"
              rules={[{ required: true, message: 'Please input your password!' }]}
            >
              <Input.Password prefix={<LockOutlined />} placeholder="Password" />
            </Form.Item>

            <Form.Item>
              <Button type="primary" htmlType="submit" loading={loading} block>
                Sign In
              </Button>
            </Form.Item>
          </Form>
        ) : (
          <div style={{ textAlign: 'center' }}>
            <SecurityScanOutlined style={{ fontSize: 48, color: '#1890ff', marginBottom: 16 }} />
            <Input
              value={code}
              onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))}
              maxLength={6}
              placeholder="000000"
              style={{
                textAlign: 'center',
                fontSize: 28,
                letterSpacing: 8,
                height: 56,
                borderRadius: 10,
                marginBottom: 16,
              }}
            />
            <Button type="primary" block size="large" loading={loading} onClick={onVerify2fa}>
              Verify
            </Button>
            <div style={{ marginTop: 12 }}>
              <Button type="link" onClick={() => { setStep('credentials'); setCode(''); setUserId(null); }}>
                Back to login
              </Button>
            </div>
          </div>
        )}

        <div className="login-footer">
          <Text type="secondary">This portal is only for authorized delivery personnel.</Text>
        </div>
      </Card>
    </div>
  );
};

export default Login;
